// Dates and numbers in Indian formats in either language: lakh and crore grouping (3,40,000; money is
// `rupees` in api.ts), and month and day names in the person's language. Digits stay Western Arabic in Hindi.
// Every rupee amount shown to a person is grouped here: `rupees` for money the Cockpit formats, and `plainAmounts`
// for amounts inside what LAMP says, whichever module wrote them, so none reaches a page as "₹62000.00".
import type { Language } from "./language.js";

/** The locale that formats for `language`. */
export const locale = (language: Language): string => (language === "hi" ? "hi-IN" : "en-IN");

/** A number in Indian grouping: 1234567 → "12,34,567". */
export const formatNumber = (value: number, language: Language, options: Intl.NumberFormatOptions = {}): string =>
  new Intl.NumberFormat(locale(language), options).format(value);

/** A date (an ISO string or Date) in the person's language, e.g. "29 Sept 2026" or "29 सित॰ 2026". */
export const formatDate = (
  date: string | Date,
  language: Language,
  options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" },
): string =>
  new Intl.DateTimeFormat(locale(language), options).format(typeof date === "string" ? new Date(date) : date);

/** A moment in India's time, in the person's language: "9 Oct 2026, 10:00 am". */
export const formatMoment = (date: string | Date, language: Language): string =>
  new Intl.DateTimeFormat(locale(language), {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  }).format(typeof date === "string" ? new Date(date) : date);

/** Whole rupees' digits in lakh and crore grouping: "1234567" → "12,34,567". */
export function indianGrouping(digits: string): string {
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return rest ? `${rest},${last3}` : last3;
}

/** A rupee amount in text: the sign and spacing after ₹, its digits however grouped, and its paise. */
const RUPEE_AMOUNT = /₹(\s?-?)(\d[\d,]*\d|\d)(\.\d{1,2})?/g;

/** Every rupee amount in `text` in Indian grouping, its paise as written: "₹62000.00" → "₹62,000.00". */
export const plainAmounts = (text: string): string =>
  text.replace(
    RUPEE_AMOUNT,
    (_, lead: string, digits: string, paise: string | undefined) =>
      `₹${lead}${indianGrouping(digits.replaceAll(",", ""))}${paise ?? ""}`,
  );

/** A rupee amount of five or more digits with no grouping, as no page may show one. */
export const UNGROUPED_RUPEES = /₹\s?-?\d{5,}/;
